export interface ArticleSection {
  heading: string;
  paragraphs: string[];
}

export interface ArticleTimelineItem {
  date: string;
  label: string;
  text: string;
}

export interface Article {
  id: string;
  title: string;
  category: string;
  status: 'reversed' | 'clarifying' | 'confirmed';
  cover: string;
  summary: string;
  heat: number;
  publishDate: string;
  author: string;
  tags: string[];
  sections: ArticleSection[];
  timeline: ArticleTimelineItem[];
  statement: string;
  statementSource: string;
}

export const articles: Article[] = [
  {
    id: 'rev-001',
    title: '网传“塌房录音”被指拼接？音频鉴定公布三段来源并不相同',
    category: 'reverse',
    status: 'reversed',
    cover:
      'https://readdy.ai/api/search-image?query=vintage%20microphone%20beside%20paper%20documents%20and%20audio%20waveform%20printout%20on%20a%20warm%20cream%20desk%20editorial%20conceptual%20photography%20muted%20tones%20soft%20studio%20light%20minimal%20composition&width=1200&height=750&seq=rev-cover-01&orientation=landscape',
    summary:
      '一段被称为“实锤”的录音在多个社交群组里被反复转发，配文称内容来自同一场私下谈话。第三方音频鉴定机构介入后给出的结论是：三段音频分别来自不同时间与不同场景，存在明显的剪辑拼接痕迹。',
    heat: 98,
    publishDate: '2026-08-28',
    author: '反转观察组',
    tags: ['音频鉴定', '拼接剪辑', '信息核实'],
    sections: [
      {
        heading: '爆料是怎么出现的',
        paragraphs: [
          '最初流出的是一条时长不足两分钟的音频，发布者给它的定位是“私下谈话实录”，并配上了一句结论性极强的描述。音频里出现了玻璃碰撞声、街道噪音和一段听不清的回应，听起来确实像“随手录下的一段对话”。',
          '由于内容带有强烈情绪指向，这条音频在数小时内被搬运到多个平台，每搬运一次，配文就多一句“已坐实”。到当天夜里，相关话题已经进入多个平台的热搜位置。',
        ],
      },
      {
        heading: '鉴定是怎么做的',
        paragraphs: [
          '当事方在第二天委托了第三方音频鉴定机构。鉴定围绕三个维度展开：底噪环境是否连续、人声频谱是否断裂、背景回声是否来自同一空间。这三个维度恰好是拼接音频最容易露怯的地方。',
          '鉴定报告指出，三段人声的底噪特征来自截然不同的声学环境，其中一段的背景噪音更接近室内空场，而另外两段带有明显的户外环境音。三段之间还存在约零点几秒的电平突变，属于典型的剪切点特征。',
        ],
      },
      {
        heading: '结论与提醒',
        paragraphs: [
          '报告结论为“三段音频不具备同一次录音的声学连续性”。随后发布原始音频的账号删除了内容，另有多个搬运账号发布了更正说明。这并不意味着讨论到此结束，而是说明“先转发再核实”的顺序本身值得反思。',
          '遇到情绪浓度极高的音频或截图时，先看三件事：有没有完整来源、有没有时间地点、有没有可验证的原始文件。把这三件事问清楚，能过滤掉相当一部分被剪辑过的“证据”。',
        ],
      },
    ],
    timeline: [
      { date: '08-25', label: '爆料出现', text: '两分钟音频在小范围群组流传，配以结论性描述。' },
      { date: '08-25', label: '快速发酵', text: '多条搬运内容进入平台热搜，讨论量数小时内翻倍。' },
      { date: '08-26', label: '当事方回应', text: '当事方表示音频与实际情况不符，并委托第三方鉴定。' },
      { date: '08-27', label: '鉴定介入', text: '围绕底噪、频谱、回声三个维度开展声学连续性分析。' },
      { date: '08-28', label: '结论公布', text: '报告认定三段音频来源不同，原始发布者删除内容。' },
    ],
    statement: '经声学连续性分析，送检音频由来源不同的三段素材拼接而成，不具备同一次录音的完整性。',
    statementSource: '第三方音频鉴定机构 鉴定摘要',
  },
  {
    id: 'rev-002',
    title: '“片场耍大牌”传闻反转：场务还原当天真实拍摄顺序',
    category: 'reverse',
    status: 'reversed',
    cover:
      'https://readdy.ai/api/search-image?query=empty%20film%20set%20with%20clapperboard%20and%20foldable%20director%20chair%20editorial%20conceptual%20photography%20warm%20cream%20background%20muted%20tones%20soft%20light%20minimal%20composition&width=1200&height=750&seq=rev-cover-02&orientation=landscape',
    summary:
      '一条匿名帖子称某剧组主演无故离场导致全组等待数小时。三天后，剧组场务公开了当天的拍摄通告单与工作群记录，显示等待的原因是设备故障与临时调景，与主演无关。',
    heat: 91,
    publishDate: '2026-08-19',
    author: '现场还原组',
    tags: ['剧组', '通告单', '匿名爆料'],
    sections: [
      {
        heading: '一条匿名帖引发的连锁反应',
        paragraphs: [
          '帖子没有任何图片，只有几行描述：某剧组主演在关键场次“说走就走”，几十号工作人员原地等待。正因为没有任何具体信息，读者反而更容易按自己的想象补全细节。',
          '随后几天，各种“知情人士”版本陆续出现，有的说等待了四小时，有的说整场戏直接取消。版本之间互相矛盾，却都被贴上了同一个标签。',
        ],
      },
      {
        heading: '通告单说了什么',
        paragraphs: [
          '场务公开的拍摄通告单显示，当天上午原定拍摄的是一场群戏，因为现场灯光设备出现故障，拍摄顺序被迫整体后移。通告单上还保留了当时的调整批注时间。',
          '工作群记录则显示，主演在等待期间一直在候场区，并按要求完成了后续夜戏的补拍。真正占用时间的是设备维修和场景重新布置。',
        ],
      },
      {
        heading: '反转之后',
        paragraphs: [
          '原帖作者在舆论转向后注销了账号，没有任何一方对其内容负责。这也是匿名爆料最常见的结局：内容消失得比出现还快，留下的却是被反复引用的截图。',
          '剧组类爆料如果只有文字没有物料，最稳妥的做法是先等一版可核对的时间表，比如通告单、工作群记录或现场照片。没有这些，讨论的其实只是描述方式，而不是事实。',
        ],
      },
    ],
    timeline: [
      { date: '08-15', label: '匿名帖出现', text: '一条无图无细节的帖子描述“主演离场”。' },
      { date: '08-17', label: '版本分裂', text: '多位“知情人士”给出互相矛盾的等待时长。' },
      { date: '08-18', label: '物料公开', text: '场务发布当日通告单与工作群记录。' },
      { date: '08-19', label: '传闻反转', text: '等待原因确认为设备故障与调景，与主演无关。' },
    ],
    statement: '当天拍摄顺序调整由灯光设备故障引起，通告单与工作群记录均保留了调整时间。',
    statementSource: '剧组场务 公开说明',
  },
  {
    id: 'rev-003',
    title: '代言解约传闻澄清：品牌方称合作按原计划续约',
    category: 'clarify',
    status: 'confirmed',
    cover:
      'https://readdy.ai/api/search-image?query=contract%20papers%20and%20fountain%20pen%20with%20a%20cup%20of%20coffee%20on%20a%20warm%20cream%20desk%20editorial%20conceptual%20photography%20muted%20amber%20tones%20soft%20studio%20light%20minimal%20composition&width=1200&height=750&seq=rev-cover-03&orientation=landscape',
    summary:
      '网上流传某品牌因舆论风波紧急撤下代言内容。品牌方对外说明：物料轮换属于既定的季度投放安排，双方合作已按原计划续约，不存在“紧急解约”。',
    heat: 84,
    publishDate: '2026-08-12',
    author: '辟谣跟进组',
    tags: ['代言', '物料轮换', '官方说明'],
    sections: [
      {
        heading: '误会从哪里来',
        paragraphs: [
          '起因是品牌官网首页的代言图被替换成了产品图。有账号据此推断“连夜撤图”，并延伸出解约、赔偿等一系列结论。图片确实被换了，但换图的原因和结论完全是两回事。',
          '这类推断有一个共同点：把观察到的现象直接等同于最戏剧化的解释，跳过了“有没有更普通的可能”这一步。',
        ],
      },
      {
        heading: '品牌方如何说明',
        paragraphs: [
          '品牌方在说明中提到，季度物料轮换在年初的投放计划里就已排期，本次替换的图片与下一阶段主推的产品线一致。说明同时附上了续约合同的关键信息。',
          '值得注意的是，品牌方并没有逐条反驳网上的猜测，而是给出了一个更硬的书面材料。在辟谣场景里，一份能点开看的文件，通常比一段情绪化回应更有说服力。',
        ],
      },
      {
        heading: '怎么看待“撤图”类爆料',
        paragraphs: [
          '物料变动是商业运营的日常动作，与舆论事件之间并不存在必然联系。判断时可以先看两点：变动是否出现在既定的排期节点上，以及品牌方是否给出了可查证的时间线。',
          '如果两点都成立，那么“紧急解约”的说法往往站不住脚。',
        ],
      },
    ],
    timeline: [
      { date: '08-09', label: '首页换图', text: '品牌官网首页代言图替换为主推产品图。' },
      { date: '08-10', label: '解读升温', text: '部分账号将其解读为“连夜撤下代言内容”。' },
      { date: '08-11', label: '品牌方说明', text: '说明换图属于既定季度轮换，并附续约信息。' },
      { date: '08-12', label: '误会澄清', text: '相关话题热度回落，讨论转向物料排期机制。' },
    ],
    statement: '本次首页图片替换为年初既定投放计划中的季度轮换，双方合作关系按原合约续约。',
    statementSource: '品牌方 对外说明',
  },
  {
    id: 'rev-004',
    title: '“后厨蟑螂”视频被指摆拍，监管部门复核结果公布',
    category: 'reverse',
    status: 'reversed',
    cover:
      'https://readdy.ai/api/search-image?query=clean%20modern%20restaurant%20kitchen%20with%20stainless%20steel%20counter%20and%20organized%20utensils%20editorial%20conceptual%20photography%20warm%20cream%20background%20muted%20tones%20soft%20light%20minimal%20composition&width=1200&height=750&seq=rev-cover-04&orientation=landscape',
    summary:
      '一段在餐饮店内拍摄的视频因画面中出现虫害被广泛转发。监管部门复核后通报：视频拍摄位置与店内实际布局不符，画面疑为人为置入，已对相关内容开展进一步核查。',
    heat: 88,
    publishDate: '2026-08-05',
    author: '事实核查组',
    tags: ['食品安全', '现场复核', '视频真伪'],
    sections: [
      {
        heading: '视频讲了什么',
        paragraphs: [
          '视频以第一视角拍摄，镜头快速掠过操作台，随后在一个角落停住。画面信息量很大，但几乎没有交代拍摄时间、门店位置和拍摄者身份。',
          '这类视频传播力极强，因为它直接指向每个人都在意的食品安全。也正因为如此，它更容易被当作“铁证”直接转发。',
        ],
      },
      {
        heading: '复核是怎么进行的',
        paragraphs: [
          '监管部门到场后调取了店内监控，并按视频中的镜头顺序逐点比对。结果显示，视频中出现的墙面结构与门店现有布局不一致，部分摆件在监控时段内并未出现在画面位置。',
          '通报同时提到，门店当期的自查记录与第三方消杀记录均可提供，未发现与视频描述相符的情况。相关内容的进一步核查仍在进行。',
        ],
      },
      {
        heading: '这不是否定监督',
        paragraphs: [
          '澄清视频真伪，并不等于降低对食品安全的要求。恰恰相反，真实、可核对的监督才能推动问题被真正解决，而摆拍式的指控会消耗公众对整个监督体系的信任。',
          '看到此类视频时，可以先确认门店名称、拍摄时间与提交渠道是否清晰。信息越完整，越值得认真对待。',
        ],
      },
    ],
    timeline: [
      { date: '08-01', label: '视频流出', text: '第一视角视频出现，未标注时间与门店信息。' },
      { date: '08-02', label: '大量转发', text: '食品安全话题迅速扩散，多地门店被牵连。' },
      { date: '08-03', label: '现场复核', text: '监管部门调取监控并逐点比对画面。' },
      { date: '08-05', label: '结果公布', text: '画面布局与门店不符，相关内容进一步核查中。' },
    ],
    statement: '经现场比对，视频中的墙面结构与门店布局不一致，未发现与描述相符的情况。',
    statementSource: '地方市场监管部门 情况通报',
  },
  {
    id: 'rev-005',
    title: '“不当言论截图”疯传，校方公布调查全过程',
    category: 'clarify',
    status: 'reversed',
    cover:
      'https://readdy.ai/api/search-image?query=laptop%20screen%20showing%20blurred%20chat%20bubbles%20on%20a%20wooden%20desk%20editorial%20conceptual%20photography%20warm%20cream%20background%20muted%20tones%20soft%20light%20minimal%20composition&width=1200&height=750&seq=rev-cover-05&orientation=landscape',
    summary:
      '一张聊天记录截图被指来自某高校课堂群，内容极具争议。校方核查后说明：截图中并非该校任何群组，头像与备注均系后期拼接，已就造谣行为向平台提交材料。',
    heat: 79,
    publishDate: '2026-07-28',
    author: '事实核查组',
    tags: ['聊天截图', '拼接伪造', '校方通报'],
    sections: [
      {
        heading: '截图为什么容易被相信',
        paragraphs: [
          '聊天截图自带“一手材料”的观感：有对话框、有昵称、有发送时间，看起来几乎是不可辩驳的。但恰恰是这些元素，都很容易通过简单的工具伪造。',
          '这条截图的问题在于头像边缘有明显的重新描边痕迹，昵称字体与平台默认字体存在细微差别，这两点都不是正常截图会出现的特征。',
        ],
      },
      {
        heading: '核查都查了什么',
        paragraphs: [
          '校方公布的核查步骤包括：比对校内所有在建群组的成员名单与昵称设置、调取被指账号的登录与发言记录、联系平台获取原始数据特征。三项核查均未匹配到截图中的内容。',
          '通报还提到，截图中出现的头像属于另一平台的公开素材，与校内任何人无关。也就是说，这张图是在一个并不存在的群组里被“造”出来的。',
        ],
      },
      {
        heading: '一条可复用的判断方法',
        paragraphs: [
          '看聊天截图时，先找三个细节：头像边缘是否自然、字体是否统一、时间戳是否能与其他内容自洽。伪造的截图很难同时把这三处都做对。',
          '更重要的是，看到截图先别急着转发，先问一句“原始群组叫什么、成员是谁”。如果连这两个问题都答不上来，那么这张图的可信度就应该打点折扣。',
        ],
      },
    ],
    timeline: [
      { date: '07-24', label: '截图出现', text: '一张聊天记录截图在多个平台被转发。' },
      { date: '07-25', label: '话题升温', text: '截图被解读为某高校课堂群内容。' },
      { date: '07-26', label: '校方核查', text: '比对群组成员、发言记录与平台数据特征。' },
      { date: '07-28', label: '通报公布', text: '确认截图为拼接伪造，已提交造谣材料。' },
    ],
    statement: '经比对，截图中群组、昵称与头像均非校内信息，系后期拼接伪造，学校已提交相关材料。',
    statementSource: '高校 情况通报',
  },
  {
    id: 'rev-006',
    title: '婚变传闻持续半年后，当事双方用联合说明画上句号',
    category: 'followup',
    status: 'confirmed',
    cover:
      'https://readdy.ai/api/search-image?query=two%20empty%20chairs%20facing%20each%20other%20in%20a%20bright%20minimal%20room%20with%20soft%20window%20light%20editorial%20conceptual%20photography%20warm%20cream%20tones%20minimal%20composition&width=1200&height=750&seq=rev-cover-06&orientation=landscape',
    summary:
      '半年来，关于两人婚姻状况的猜测从未停止，从同框频率到行程安排都被逐帧解读。最终双方发布联合说明，明确了当前的生活状态，也请外界停止对家人的打扰。',
    heat: 76,
    publishDate: '2026-07-20',
    author: '真相后续组',
    tags: ['长期传闻', '联合说明', '隐私边界'],
    sections: [
      {
        heading: '传闻是怎样被“养大”的',
        paragraphs: [
          '最初只是一次公开活动上两人没有同框，随后被解读为“关系生变”。此后每一次行程不同步、每一张缺少对方的照片，都会被自动纳入这条叙事线。',
          '当一条传闻持续足够久，它就不再需要证据了——新出现的任何信息，都会被解释成“果然如此”。这是长期传闻最典型的自我强化机制。',
        ],
      },
      {
        heading: '联合说明说了什么',
        paragraphs: [
          '联合说明没有回应传闻中的各种细节猜测，而是直接说明了两人当前的生活状态，并明确表示不会再就个人生活作进一步回应。',
          '这种处理方式值得注意：辟谣并不需要逐条反驳，尤其是当猜测本身没有事实基础时。给出一个明确、完整、一次性的说明，往往比反复回应更有效。',
        ],
      },
      {
        heading: '关于边界',
        paragraphs: [
          '在公众人物的私人生活议题上，“有讨论价值”和“应该被讨论”之间存在明显区别。把猜测当作娱乐消遣时，被讨论的人及其家人承受的是真实的打扰。',
          '报道与围观的分界线其实很简单：是否以可核实的事实为基础，是否对当事人保有基本的尊重。',
        ],
      },
    ],
    timeline: [
      { date: '01-18', label: '传闻起点', text: '一次公开活动未同框，被解读为关系变化。' },
      { date: '03-09', label: '持续放大', text: '行程差异被逐帧分析，话题周期性回热。' },
      { date: '06-30', label: '呼声出现', text: '部分网友开始呼吁停止对私人生活的过度解读。' },
      { date: '07-20', label: '画上句号', text: '双方发布联合说明，明确现状并请停止打扰。' },
    ],
    statement: '感谢关心，我们已就当前生活状态做出说明，此后不再回应个人生活相关内容，也请给予家人基本的空间。',
    statementSource: '当事双方 联合说明',
  },
  {
    id: 'rev-007',
    title: '“捐款去向”质疑被回应：公益机构公布完整流水',
    category: 'followup',
    status: 'reversed',
    cover:
      'https://readdy.ai/api/search-image?query=receipt%20documents%20and%20magnifying%20glass%20on%20a%20warm%20cream%20table%20editorial%20conceptual%20photography%20muted%20amber%20tones%20soft%20studio%20light%20minimal%20composition&width=1200&height=750&seq=rev-cover-07&orientation=landscape',
    summary:
      '有账号质疑一场公益筹款的资金去向，截图只截取了部分页面。机构随后公布了完整的收支流水与项目执行明细，质疑中的多处“缺口”实为分期拨付与跨页截图造成的误读。',
    heat: 73,
    publishDate: '2026-07-11',
    author: '真相后续组',
    tags: ['公益', '财务公开', '完整流水'],
    sections: [
      {
        heading: '质疑从哪里来',
        paragraphs: [
          '质疑帖放出了几张公示页面的截图，指出“筹款金额”与“支出金额”之间存在明显差额。截图本身是真实的，问题出在它只截取了页面的其中一屏。',
          '在页面下方，其实有详细的拨付时间说明：款项按项目里程碑分期拨付，剩余部分属于尚未到期的执行阶段。跨页截取恰好把这段说明裁掉了。',
        ],
      },
      {
        heading: '机构如何回应',
        paragraphs: [
          '机构没有停留在口头解释，而是直接上传了完整账目文件：每一笔收入的来源、每一笔支出的时间与对象、项目每个阶段的实际进展，都可以逐项对照。',
          '同时公布的还有项目现场记录与受益方确认材料。完整流水的一个好处是，它把讨论从“信不信”拉回到“对不对”——任何人都可以自己去核对那一栏数字。',
        ],
      },
      {
        heading: '公开是最好的澄清',
        paragraphs: [
          '公益领域的质疑非常正常，回应质疑最有效的方式也从来不是情绪，而是数据。只要账目足够完整、单位足够清晰、时间足够细，大部分误解会自行消失。',
          '对读者来说，看到财务类质疑时，先确认截图是否完整、单位是否一致、时间范围是否对齐。这三个问题能滤掉相当一部分“看起来像问题的问题”。',
        ],
      },
    ],
    timeline: [
      { date: '07-06', label: '质疑出现', text: '帖子截取部分公示页面，指出金额差额。' },
      { date: '07-07', label: '讨论扩大', text: '资金去向话题升温，多个账号参与解读。' },
      { date: '07-09', label: '完整公开', text: '机构上传完整收支流水与项目执行明细。' },
      { date: '07-11', label: '误读澄清', text: '差额确认为分期拨付，质疑帖补充更正说明。' },
    ],
    statement: '现已公开全部收支流水与项目执行明细，公示页面的差额为按里程碑分期拨付的未到期部分。',
    statementSource: '公益机构 公开说明',
  },
  {
    id: 'rev-008',
    title: '综艺“恶意剪辑”争议：未播出素材公开后讨论转向',
    category: 'observe',
    status: 'clarifying',
    cover:
      'https://readdy.ai/api/search-image?query=video%20editing%20timeline%20on%20a%20monitor%20in%20a%20dim%20studio%20with%20headphones%20editorial%20conceptual%20photography%20warm%20cream%20and%20amber%20tones%20soft%20light%20minimal%20composition&width=1200&height=750&seq=rev-cover-08&orientation=landscape',
    summary:
      '一期综艺播出后，有观众认为某段互动被刻意剪辑成冲突。节目组公开了该段落的未播出素材，画面显示讨论过程比成片更长，双方也有明显的缓和与玩笑成分。',
    heat: 69,
    publishDate: '2026-07-02',
    author: '舆情观察组',
    tags: ['综艺剪辑', '未播出素材', '语境缺失'],
    sections: [
      {
        heading: '成片与素材差了点什么',
        paragraphs: [
          '成片为了节奏会把两个小时的内容压缩到几分钟，这个过程中被省略的往往是“过渡”和“缓和”。观众看到的冲突感，很多时候来自语境的缺失，而不是当事人的本意。',
          '这一期被质疑的段落，成片只保留了语气最紧张的两句对话，前后各有一段自然的解释被剪掉，于是观感就完全不同了。',
        ],
      },
      {
        heading: '素材公开之后',
        paragraphs: [
          '节目组公开的素材完整呈现了这段讨论：两人先有分歧，随后互相解释，最后以玩笑收尾。公开之后，原本“站队式”的讨论逐渐转向对剪辑机制本身的讨论。',
          '这类争议的特别之处在于，它不是谁在说谎，而是信息在压缩过程中被改变了形状。判断时更适合问“被剪掉的是什么”，而不是急着判断谁对谁错。',
        ],
      },
      {
        heading: '一个长期成立的规律',
        paragraphs: [
          '凡是由剪辑产生的画面，都应当默认存在“未被看到的上下文”。这不是要否定观众的感受，而是提醒我们把感受和事实分开来看。',
          '当节目组愿意公开未播出素材时，这本身就是一种可核实的态度，值得被认真对待。',
        ],
      },
    ],
    timeline: [
      { date: '06-27', label: '节目播出', text: '一期节目播出，某段互动被认为剪辑成冲突。' },
      { date: '06-28', label: '争议升温', text: '观众分阵营讨论，相关片段被大量二次剪辑。' },
      { date: '06-30', label: '素材公开', text: '节目组放出该段未播出素材完整版。' },
      { date: '07-02', label: '讨论转向', text: '焦点从“谁对谁错”转向剪辑机制与语境缺失。' },
    ],
    statement: '为回应观众关切，现公开该段落未播出素材，完整过程包含解释与缓和环节，成片因时长限制未予保留。',
    statementSource: '节目制作方 说明',
  },
];

export const getArticleById = (id: string): Article | undefined =>
  articles.find((item) => item.id === id);

export const getRelatedArticles = (id: string, limit = 3): Article[] => {
  const current = getArticleById(id);
  if (!current) return articles.slice(0, limit);
  const sameCategory = articles.filter((item) => item.id !== id && item.category === current.category);
  const others = articles.filter((item) => item.id !== id && item.category !== current.category);
  return [...sameCategory, ...others].slice(0, limit);
};

export const hotArticles = [...articles].sort((a, b) => b.heat - a.heat);